import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CaseStudies() {
  const [studies, setStudies] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStudies(); }, [filter]);

  const fetchStudies = async () => {
    try {
      const params = filter !== 'all' ? `?industry=${filter}` : '';
      const { data } = await axios.get(`${API}/case-studies${params}`);
      setStudies(data);
    } catch {} finally { setLoading(false); }
  };

  const industries = ['all', 'Automotive', 'Real Estate', 'Medical', 'Tech', 'Finance', 'Legal', 'Retail', 'Manufacturing', 'Energy'];

  return (
    <div className="min-h-screen bg-[#090E33]" data-testid="case-studies-page">
      <div className="border-b border-white/10 bg-[#111827]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-8">
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-2">
            Case <span className="text-[#FF4343]">Studies</span>
          </h1>
          <p className="text-[#9CA3AF] text-base max-w-2xl">
            Real results from real campaigns. See how Freddy Leads delivers for brands across industries.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-8">
        {/* Filter */}
        <div className="flex gap-3 mb-8 flex-wrap">
          {industries.map(i => (
            <button key={i} onClick={() => setFilter(i)} data-testid={`cs-filter-${i}`}
              className={`text-xs font-mono uppercase tracking-wider px-4 py-2 border transition-colors ${
                filter === i ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#9CA3AF] hover:text-white'
              }`}>
              {i === 'all' ? 'All' : i}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1,2].map(i => (
              <div key={i} className="bg-[#111827] border border-white/10 p-8 animate-pulse">
                <div className="h-4 bg-white/10 w-1/3 mb-4" /><div className="h-20 bg-white/10 mb-4" /><div className="h-4 bg-white/10 w-2/3" />
              </div>
            ))}
          </div>
        ) : studies.length === 0 ? (
          <div className="text-center py-20"><p className="text-[#9CA3AF]">No case studies found.</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {studies.map(cs => (
              <div key={cs.id} className="bg-[#111827] border border-white/10 p-5 sm:p-8 border-l-[3px] border-l-[#FF4343] hover:border-white/20 transition-colors" data-testid={`case-study-${cs.id}`}>
                <div className="flex items-center flex-wrap gap-2 sm:gap-3 mb-5 sm:mb-6">
                  <span className="text-[10px] sm:text-xs font-mono uppercase tracking-wider px-2 py-1 bg-white/5 text-[#9CA3AF]">{cs.industry}</span>
                  <span className="text-[10px] sm:text-xs font-mono uppercase tracking-wider text-[#6B7280]">{cs.campaign_type}</span>
                  {cs.is_featured && <span className="text-[10px] sm:text-xs font-mono uppercase tracking-wider text-[#FF4343]">Featured</span>}
                </div>
                <div className="grid grid-cols-3 gap-2 sm:gap-6 mb-5 sm:mb-6">
                  <div className="min-w-0">
                    <span className="block font-mono text-lg sm:text-3xl font-bold text-[#FF4343] tabular-nums leading-tight truncate">${cs.total_spend?.toLocaleString()}</span>
                    <p className="text-[10px] sm:text-xs text-[#6B7280] mt-1 font-mono uppercase tracking-wider">Total Spend</p>
                  </div>
                  <div className="min-w-0">
                    <span className="block font-mono text-lg sm:text-3xl font-bold text-white tabular-nums leading-tight truncate">{(cs.total_impressions / 1000).toFixed(0)}K</span>
                    <p className="text-[10px] sm:text-xs text-[#6B7280] mt-1 font-mono uppercase tracking-wider">Impressions</p>
                  </div>
                  <div className="min-w-0">
                    <span className="block font-mono text-lg sm:text-3xl font-bold text-[#10B981] tabular-nums leading-tight truncate">${cs.cpm}</span>
                    <p className="text-[10px] sm:text-xs text-[#6B7280] mt-1 font-mono uppercase tracking-wider">CPM</p>
                  </div>
                </div>
                {cs.executive_count && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4 text-xs sm:text-sm text-[#6B7280]">
                    <span>{cs.executive_count} executives</span>
                    {cs.avg_engagement_rate && <span>{cs.avg_engagement_rate}% avg engagement</span>}
                  </div>
                )}
                <p className="text-sm text-[#9CA3AF] italic border-t border-white/10 pt-4">"{cs.outcome_quote}"</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Powered by Freddy Media */}
      <section className="py-8 sm:py-14 bg-white" data-testid="powered-by-banner">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-5">
              <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/lnrtssp0_freddy-logo-script.png" alt="Freddy Media" className="h-10 sm:h-14" />
              <div className="h-10 w-px bg-[#090E33]/10 hidden sm:block" />
              <div>
                <p className="text-[#090E33] text-sm font-semibold tracking-wide">Powered by Freddy Media</p>
                <p className="text-[#090E33]/50 text-xs mt-0.5">The Executive Endorsement Company</p>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/0razqlkv_freddy-logo-seal-symbol.png" alt="" className="h-11 w-11 opacity-80" />
              <Link to="/for-brands" data-testid="powered-by-cta"
                className="border-2 border-[#090E33] bg-transparent text-[#090E33] hover:bg-[#090E33] hover:text-white px-6 py-2.5 text-sm font-semibold transition-all duration-300">
                Launch a Campaign
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
